import {DomainObject} from 'shared/store';
import { computed, observable, makeObservable } from 'mobx';
import {Employee} from 'main/stores/employees';
import {rangeDate} from 'shared/tools';
import TimelineEntry from 'main/stores/performance_goals/timeline_entries/TimelineEntry';
import Metric from './Metric';
import MetricViewModel from './MetricViewModel';
import {formatPercentage, t} from 'shared/core';
import _ from 'lodash';

class Goal extends DomainObject {
  title;
  description;
  startDate;
  endDate;
  completeness;
  createdAt;
  completedAt;
  updatedAt;
  employee;
  parentGoal;
  parentGoalTitle;
  metrics = [];
  subgoals = [];
  goalTimelineEntries = [];

  canViewFullDetails;

  _relations = {
    employee: Employee,
    metrics: [Metric],
    parentGoal: Goal,
    subgoals: [Goal],
    _dates: ['startDate', 'endDate', 'createdAt', 'completedAt', 'updatedAt']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      title: observable,
      description: observable,
      startDate: observable,
      endDate: observable,
      completeness: observable,
      createdAt: observable,
      completedAt: observable,
      updatedAt: observable,
      employee: observable,
      parentGoal: observable,
      parentGoalTitle: observable,
      metrics: observable,
      subgoals: observable,
      goalTimelineEntries: observable,
      canViewFullDetails: observable,
      formattedCompleteness: computed,
      complete: computed,
      inProgress: computed,
      upcoming: computed,
      incomplete: computed,
      sortedMetrics: computed,
      sortedMetricViewModels: computed,
      sortedSubgoals: computed,
      startDateView: computed,
      endDateView: computed,
      updatedAtView: computed,
      titleView: computed,
      sortedGoalTimelineEntries: computed,
      progressBarItems: computed,
      titleWithEmployeeName: computed
    });

    this.merge(other);
  }

  _afterMerge() {
    this.goalTimelineEntries = this.goalTimelineEntries.map(entry => TimelineEntry.fromJS(entry));
  }

  get formattedCompleteness() {
    return formatPercentage(this.completeness/100);
  }

  get complete() {
    return this.status === 'complete';
  }

  get inProgress() {
    return this.status === 'in_progress';
  }

  get upcoming() {
    return this.status === 'upcoming';
  }

  get incomplete() {
    return this.status === 'incomplete';
  }

  get sortedMetrics() {
    return _.sortBy(this.metrics, 'order');
  }

  get sortedMetricViewModels() {
    return _.sortBy(
      _.map(this.metrics, metric => new MetricViewModel(metric)),
      'data.order'
    );
  }

  get sortedSubgoals() {
    return _.sortBy(this.subgoals, 'order');
  }

  get startDateView() {
    return rangeDate(this.startDate);
  }

  get endDateView() {
    return rangeDate(this.endDate);
  }

  get updatedAtView() {
    return rangeDate(this.updatedAt);
  }

  get titleView() {
    if (this.title) {
      return this.title;
    } else {
      return t('goals.Untitled Goal');
    }
  }

  get sortedGoalTimelineEntries() {
    return _.orderBy(this.goalTimelineEntries, 'createdAt', 'desc');
  }

  get progressBarItems() {
    return [
      { label: 1, value: this.completeness, color: '#08BAD3' },
      { label: 2, value: 100 - this.completeness, color: '#EBEFF1' }
    ];
  }

  get titleWithEmployeeName() {
    if (!this.employee) return this.title;

    return `${this.title} (${this.employee.name})`;
  }
}

export default Goal;
